import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { accountKeywordsService } from '@/services/accountKeywordsService';
import {
  AccountKeyword,
  KeywordsListFilters,
  CreateKeywordData,
  UpdateKeywordData,
} from '@/types/accountKeywords';

/**
 * Query key factory for account keywords
 */
export const keywordsKeys = {
  all: ['account-keywords'] as const,
  lists: () => [...keywordsKeys.all, 'list'] as const,
  list: (filters?: KeywordsListFilters) => [...keywordsKeys.lists(), filters] as const,
  details: () => [...keywordsKeys.all, 'detail'] as const,
  detail: (id: number) => [...keywordsKeys.details(), id] as const,
};

/**
 * Hook to fetch list of keywords
 */
export const useKeywords = (
  filters?: KeywordsListFilters,
  options?: Omit<UseQueryOptions<AccountKeyword[]>, 'queryKey' | 'queryFn'>
) => {
  const enabled = options?.enabled !== false && !!filters?.account_id;

  return useQuery<AccountKeyword[]>({
    queryKey: keywordsKeys.list(filters),
    queryFn: () => accountKeywordsService.getKeywords(filters),
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
    ...options,
  });
};

/**
 * Hook to fetch a specific keyword
 */
export const useKeyword = (
  id: number,
  options?: Omit<UseQueryOptions<AccountKeyword>, 'queryKey' | 'queryFn'>
) => {
  const enabled = options?.enabled !== false && id > 0;

  return useQuery<AccountKeyword>({
    queryKey: keywordsKeys.detail(id),
    queryFn: () => accountKeywordsService.getKeyword(id),
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
    ...options,
  });
};

/**
 * Hook to create a new keyword
 */
export const useCreateKeyword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateKeywordData) => accountKeywordsService.createKeyword(data),
    onSuccess: (newKeyword) => {
      // Invalidate all keywords lists to refetch
      queryClient.invalidateQueries({
        queryKey: keywordsKeys.lists(),
      });

      // Optionally, add the new keyword to the cache
      queryClient.setQueryData(keywordsKeys.detail(newKeyword.id), newKeyword);
    },
    onError: (error) => {
      console.error('[useCreateKeyword] Mutation error:', error);
    },
  });
};

/**
 * Hook to update an existing keyword
 */
export const useUpdateKeyword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateKeywordData }) =>
      accountKeywordsService.updateKeyword(id, data),
    onSuccess: (updatedKeyword, variables) => {
      // Update the detail cache
      queryClient.setQueryData(keywordsKeys.detail(variables.id), updatedKeyword);

      // Invalidate all lists to refetch
      queryClient.invalidateQueries({
        queryKey: keywordsKeys.lists(),
      });
    },
    onError: (error) => {
      console.error('[useUpdateKeyword] Mutation error:', error);
    },
  });
};

/**
 * Hook to delete a keyword
 */
export const useDeleteKeyword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => accountKeywordsService.deleteKeyword(id),
    onSuccess: (_, deletedId) => {
      // Remove from detail cache
      queryClient.removeQueries({
        queryKey: keywordsKeys.detail(deletedId),
      });

      // Invalidate all lists to refetch
      queryClient.invalidateQueries({
        queryKey: keywordsKeys.lists(),
      });
    },
    onError: (error) => {
      console.error('[useDeleteKeyword] Mutation error:', error);
    },
  });
};
